import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  ButtonIcon,
  ButtonSelect,
  DateAndTimePicker,
  Icon,
  Menu,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { addYears } from 'date-fns';

import { useInvertedLocalize } from '../../../../shared/hooks';
import type { Finding, LogEntry } from '../../data-layer/insightDashboardTypes';
import {
  formatTickLabel,
  parseFullTimestamp,
} from '../../data-layer/insightDashboardUtils';

import './SessionAreaChart.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SessionAreaChartProps {
  logEntries: LogEntry[];
  findings: Finding[];
  onIncidentClick?: (
    severity: 'CRITICAL' | 'HIGH' | 'MEDIUM',
    findingId: number,
    sessionColorMap?: Record<string, string>
  ) => void;
  onSessionColorMapReady?: (colorMap: Record<string, string>) => void;
  onSliceChange?: (
    timeRange: { startEpoch: number; endEpoch: number } | null,
    visibleFindingIds?: number[]
  ) => void;
  className?: string;
}

const defaultProps: Partial<SessionAreaChartProps> = {
  onIncidentClick: undefined,
  onSessionColorMapReady: undefined,
  onSliceChange: undefined,
  className: undefined,
};

interface IncidentMarker {
  findingId: number;
  title: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM';
  epoch: number;
  sessionId: string;
  phase: string;
  riskScore: number;
}

interface BucketData {
  epoch: number;
  label: string;
  sessions: Record<string, number>;
}

interface SessionMeta {
  id: string;
  totalEvents: number;
  highSeverityCount: number;
  highSeverityRatio: number;
  isWarm: boolean;
  color: string;
  fillColor: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

// Cool palette — normal/low-risk sessions
// Spaced across blue-cyan-teal-indigo-violet with alternating lightness for contrast
const COOL_PALETTE = [
  'hsl(215, 65%, 58%)', // Steel blue
  'hsl(175, 55%, 48%)', // Teal
  'hsl(255, 50%, 62%)', // Soft violet
  'hsl(195, 60%, 50%)', // Cyan
  'hsl(145, 45%, 50%)', // Sage green
  'hsl(230, 55%, 55%)', // Indigo
  'hsl(165, 50%, 45%)', // Dark teal
  'hsl(205, 55%, 62%)', // Light steel
  'hsl(280, 40%, 58%)', // Lavender
  'hsl(185, 50%, 44%)', // Deep cyan
  'hsl(155, 48%, 52%)', // Mint
  'hsl(240, 42%, 60%)', // Periwinkle
];

// Warm palette — suspicious/attacker/exfiltration sessions
// Red-orange-amber range, clearly distinct from cool palette
const WARM_PALETTE = [
  'hsl(5, 72%, 55%)', // Crimson
  'hsl(30, 75%, 52%)', // Burnt orange
  'hsl(350, 65%, 50%)', // Rose
  'hsl(15, 78%, 50%)', // Vermillion
  'hsl(45, 70%, 50%)', // Amber
  'hsl(0, 60%, 48%)', // Dark red
  'hsl(25, 70%, 46%)', // Rust
  'hsl(340, 60%, 52%)', // Magenta-rose
];

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'var(--id-severity-critical)',
  HIGH: 'var(--id-severity-high)',
  MEDIUM: 'var(--id-severity-medium)',
};

const PHASE_LABELS: Record<string, string> = {
  reconnaissance: 'Recon',
  weaponization: 'Weaponize',
  exploitation: 'Exploit',
  anti_forensics: 'Anti-Forensics',
};

const CHART_PADDING = { top: 24, right: 30, bottom: 50, left: 60 };
const CHART_HEIGHT = 340;

const TRANSACTION_INSIGHT = 'Transaction Insight';
const SESSIONS_PREFIX = 'sessions (';
const VISIBLE_SUFFIX = 'visible)';
const INCIDENTS = 'incidents';
const CRITICAL = 'critical';
const HIGH = 'high';
const MEDIUM = 'medium';
const TO_LABEL = 'to';
const ALL_LABEL = 'All';
const RISKY_PREFIX = 'Risky (';
const CLOSE_PAREN = ')';
const SESSION_PREFIX = 'S-';
const EVENTS_LABEL = 'events';
const TOTAL_COLON = 'Total:';
const RISK_LABEL = 'Risk';
const HASH = '#';
const MIDDOT = '\u00B7';
const SESSION_WORD = 'Session';
const EVENTS_PER_BUCKET = 'Events / bucket';
const TIME_LABEL = 'Time';
const NO_DATA_MSG = 'No data available for the selected date range';
const TIME_PAREN = 'Time (';
const INTERVALS_CLOSE = 'intervals)';
const CHART_WIDTH = 960;
const BUCKET_SIZE_OPTIONS = [
  { label: '1 min', value: 60 },
  { label: '2 min', value: 120 },
  { label: '5 min', value: 300 },
];

const HIGH_SEVERITY_THRESHOLD = 0.1; // 10%

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function makeFillColor(baseColor: string): string {
  // Convert hsl(...) to hsla(..., 0.45) — no border, so slightly more opaque
  return baseColor.replace('hsl(', 'hsla(').replace(')', ', 0.45)');
}

function computeSessionMetas(
  logEntries: LogEntry[],
  sessionIds: string[]
): SessionMeta[] {
  const countMap: Record<string, { total: number; high: number }> = {};

  sessionIds.forEach((sid) => {
    countMap[sid] = { total: 0, high: 0 };
  });

  logEntries.forEach((e) => {
    countMap[e.sessionId].total += 1;
    const sev = e.severity.toUpperCase();

    if (sev === 'HIGH' || sev === 'CRITICAL') {
      countMap[e.sessionId].high += 1;
    }
  });

  let coolIdx = 0;
  let warmIdx = 0;

  // Sort by highSeverityRatio ascending (normal first, dangerous last)
  const sorted = sessionIds
    .map((id) => {
      const { total, high } = countMap[id];
      const ratio = total > 0 ? high / total : 0;

      return {
        id,
        totalEvents: total,
        highSeverityCount: high,
        highSeverityRatio: ratio,
      };
    })
    .sort(
      (a, b) =>
        a.highSeverityRatio - b.highSeverityRatio ||
        b.totalEvents - a.totalEvents
    );

  return sorted.map((s) => {
    const isWarm = s.highSeverityRatio >= HIGH_SEVERITY_THRESHOLD;
    let color: string;

    if (isWarm) {
      color = WARM_PALETTE[warmIdx % WARM_PALETTE.length];
      warmIdx += 1;
    } else {
      color = COOL_PALETTE[coolIdx % COOL_PALETTE.length];
      coolIdx += 1;
    }

    return {
      ...s,
      isWarm,
      color,
      fillColor: makeFillColor(color),
    };
  });
}

function buildBuckets(
  logEntries: LogEntry[],
  sessionIds: string[],
  bucketSizeSec: number
): BucketData[] {
  if (logEntries.length === 0 || sessionIds.length === 0) {
    return [];
  }

  const filtered = logEntries.filter((e) => sessionIds.includes(e.sessionId));

  let minEpoch = Infinity;
  let maxEpoch = -Infinity;

  filtered.forEach((e) => {
    const t = parseFullTimestamp(e.logCreationDateTime);

    if (t > 0 && t < minEpoch) {
      minEpoch = t;
    }

    if (t > 0 && t > maxEpoch) {
      maxEpoch = t;
    }
  });

  if (minEpoch === Infinity) {
    return [];
  }

  const bucketStart = Math.floor(minEpoch / bucketSizeSec) * bucketSizeSec;
  const bucketEnd = Math.ceil((maxEpoch + 1) / bucketSizeSec) * bucketSizeSec;
  const count = Math.ceil((bucketEnd - bucketStart) / bucketSizeSec);
  const rangeMin = (bucketEnd - bucketStart) / 60;

  const buckets: BucketData[] = [];

  Array.from({ length: count }).forEach((_, i) => {
    const epoch = bucketStart + i * bucketSizeSec;
    const sessions: Record<string, number> = {};

    sessionIds.forEach((sid) => {
      sessions[sid] = 0;
    });

    buckets.push({ epoch, label: formatTickLabel(epoch, rangeMin), sessions });
  });

  filtered.forEach((e) => {
    const t = parseFullTimestamp(e.logCreationDateTime);

    if (t > 0) {
      const idx = Math.min(
        count - 1,
        Math.floor((t - bucketStart) / bucketSizeSec)
      );

      buckets[idx].sessions[e.sessionId] += 1;
    }
  });

  return buckets;
}

function buildIncidentMarkers(
  findings: Finding[],
  logEntries: LogEntry[],
  sessionIds: string[]
): IncidentMarker[] {
  const markers: IncidentMarker[] = [];

  findings.forEach((f) => {
    if (
      f.severity === 'CRITICAL' ||
      f.severity === 'HIGH' ||
      f.severity === 'MEDIUM'
    ) {
      let epoch = 0;
      let primarySession = '';

      if (f.steps.length > 0 && f.steps[0].timestamp) {
        const ts = f.steps[0].timestamp;
        const parts = ts.split(':');
        const h = parseInt(parts[0] ?? '0', 10);
        const m = parseInt(parts[1] ?? '0', 10);
        const s = parseInt(parts[2] ?? '0', 10);

        primarySession = f.steps[0].sessionId ?? '';

        const refEntry = logEntries.find(
          (e) =>
            e.sessionId === primarySession &&
            parseFullTimestamp(e.startTimeUtc) > 0
        );

        if (refEntry) {
          const base = parseFullTimestamp(refEntry.startTimeUtc);
          const baseDate = new Date(base * 1000);
          const dayStart =
            new Date(
              baseDate.getFullYear(),
              baseDate.getMonth(),
              baseDate.getDate()
            ).getTime() / 1000;

          epoch = dayStart + h * 3600 + m * 60 + s;
        }
      }

      if (epoch === 0 && f.evidenceLogIds?.length) {
        const entry = logEntries.find((e) => e.logId === f.evidenceLogIds?.[0]);

        if (entry) {
          epoch = parseFullTimestamp(entry.logCreationDateTime);
          primarySession = entry.sessionId;
        }
      }

      if (epoch > 0 && sessionIds.includes(primarySession)) {
        markers.push({
          findingId: f.id,
          title: f.title,
          severity: f.severity,
          epoch,
          sessionId: primarySession,
          phase: f.phase,
          riskScore: f.riskScore,
        });
      }
    }
  });

  return markers.sort((a, b) => a.epoch - b.epoch);
}

/* ------------------------------------------------------------------ */
/*  SVG Path builders                                                  */
/* ------------------------------------------------------------------ */

function buildAreaPath(
  points: { x: number; y0: number; y1: number }[]
): string {
  if (points.length === 0) {
    return '';
  }

  // Top line (left to right)
  let d = `M ${points[0].x} ${points[0].y1}`;

  points.slice(1).forEach((curr, idx) => {
    const prev = points[idx];
    const cpx = (prev.x + curr.x) / 2;

    d += ` C ${cpx} ${prev.y1}, ${cpx} ${curr.y1}, ${curr.x} ${curr.y1}`;
  });

  // Bottom line (right to left)
  [...points].reverse().forEach((curr, revIdx) => {
    const origIdx = points.length - 1 - revIdx;

    if (origIdx === points.length - 1) {
      d += ` L ${curr.x} ${curr.y0}`;
    } else {
      const next = points[origIdx + 1];
      const cpx = (next.x + curr.x) / 2;

      d += ` C ${cpx} ${next.y0}, ${cpx} ${curr.y0}, ${curr.x} ${curr.y0}`;
    }
  });

  d += ' Z';

  return d;
}

/* ------------------------------------------------------------------ */
/*  Stacked area computation                                           */
/* ------------------------------------------------------------------ */

interface StackedLayerPoints {
  sessionId: string;
  points: { x: number; y0: number; y1: number }[];
}

function computeStackedLayers(
  buckets: BucketData[],
  orderedSessionIds: string[],
  hiddenSessions: Set<string>,
  xScale: (idx: number) => number,
  yScale: (val: number) => number
): StackedLayerPoints[] {
  const layers: StackedLayerPoints[] = [];

  orderedSessionIds.forEach((sid) => {
    const isHidden = hiddenSessions.has(sid);
    const points = buckets.map((b, i) => {
      // Compute cumulative base from all previous visible sessions
      let cumulative = 0;

      layers.forEach((layer) => {
        const prevBucket = buckets[i];
        const prevSid = layer.sessionId;

        if (!hiddenSessions.has(prevSid)) {
          cumulative += prevBucket.sessions[prevSid];
        }
      });

      const value = isHidden ? 0 : b.sessions[sid];

      return {
        x: xScale(i),
        y0: yScale(cumulative),
        y1: yScale(cumulative + value),
      };
    });

    layers.push({ sessionId: sid, points });
  });

  return layers;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * Stacked session activity over time with zoom, crosshair tooltip, and incident markers.
 */
function SessionAreaChart({
  logEntries,
  findings,
  onIncidentClick,
  onSessionColorMapReady,
  onSliceChange,
  className,
}: SessionAreaChartProps) {
  const l = useInvertedLocalize();
  const containerRef = useRef<HTMLDivElement>(null);
  const [bucketSize, setBucketSize] = useState(120);

  // Default date range = current day (today 00:00:00 to 23:59:59)
  const todayDefault = useMemo(() => {
    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000 - 1);

    return { start: dayStart, end: dayEnd };
  }, []);

  // Date & time filter state — defaults to today
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(
    todayDefault.start
  );
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(
    todayDefault.end
  );

  const [isDateFilterVisible, setIsDateFilterVisible] = useState(false);
  const dateRangeMax = useMemo(() => addYears(new Date(), 1), []);
  const dateRangeMin = useMemo(() => addYears(new Date(), -5), []);
  const [hoveredMarker, setHoveredMarker] = useState<IncidentMarker | null>(
    null
  );
  const [hoveredBucket, setHoveredBucket] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);
  const [hiddenSessions, setHiddenSessions] = useState<Set<string>>(new Set());
  const [sessionFilter, setSessionFilter] = useState<string>('all');
  const [isSessionDropdownOpen, setIsSessionDropdownOpen] = useState(false);
  const sessionDropdownRef = useRef<HTMLDivElement>(null);

  // Zoom state — direct slice indices (exact bucket index range)
  // null = full view, otherwise exact bucket index range
  const [sliceRange, setSliceRange] = useState<{
    start: number;
    end: number;
  } | null>(null);

  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [selectionPct, setSelectionPct] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const dragMode = useRef<'select' | 'pan'>('select');
  const dragStartX = useRef(0);
  const dragStartSlice = useRef<{ start: number; end: number } | null>(null);

  const isZoomed = sliceRange !== null;

  const resetZoom = useCallback(() => {
    setSliceRange(null);
  }, []);

  // Zoom in — shrink the visible range by 25% from center
  const zoomIn = useCallback(() => {
    setSliceRange((prev) => {
      const total = allBucketsLengthRef.current;

      if (total <= 2) {
        return prev;
      }

      // If not zoomed, start by showing center 50%
      const currentStart = prev ? prev.start : 0;
      const currentEnd = prev ? prev.end : total - 1;
      const range = currentEnd - currentStart;

      if (range <= 2) {
        return prev ?? { start: currentStart, end: currentEnd };
      }

      const shrink = Math.max(1, Math.floor(range * 0.25));
      const newStart = currentStart + Math.floor(shrink / 2);
      const newEnd = currentEnd - Math.ceil(shrink / 2);

      return { start: newStart, end: Math.max(newStart + 1, newEnd) };
    });
  }, []);

  // Zoom out — expand the visible range by 50%, clamped to allBuckets
  const zoomOut = useCallback(() => {
    setSliceRange((prev) => {
      if (!prev) {
        return null;
      }

      const total = allBucketsLengthRef.current;
      const range = prev.end - prev.start;
      const expand = Math.max(1, Math.floor(range * 0.5));
      const newStart = Math.max(0, prev.start - Math.floor(expand / 2));
      const newEnd = Math.min(total - 1, prev.end + Math.ceil(expand / 2));

      // If we've expanded to full range, reset
      if (newStart === 0 && newEnd >= total - 1) {
        return null;
      }

      return { start: newStart, end: newEnd };
    });
  }, []);

  // Ref to track allBuckets length for zoomOut without stale closure
  const allBucketsLengthRef = useRef(0);

  // Filter log entries by date range when user selects start/end dates
  const filteredLogEntries = useMemo(() => {
    if (!filterStartDate && !filterEndDate) {
      return logEntries;
    }

    const startEpoch = filterStartDate ? filterStartDate.getTime() / 1000 : 0;
    const endEpoch = filterEndDate ? filterEndDate.getTime() / 1000 : Infinity;

    return logEntries.filter((e) => {
      const epoch = parseFullTimestamp(e.logCreationDateTime || e.startTimeUtc);

      return epoch >= startEpoch && epoch <= endEpoch;
    });
  }, [logEntries, filterStartDate, filterEndDate]);

  // Discover ALL sessions from filtered entries
  const allSessionIds = useMemo(() => {
    const set = new Set<string>();

    filteredLogEntries.forEach((e) => {
      if (e.sessionId) {
        set.add(e.sessionId);
      }
    });

    return Array.from(set).sort();
  }, [filteredLogEntries]);

  // Compute session metadata with severity info and color assignments
  const sessionMetas = useMemo(
    () => computeSessionMetas(filteredLogEntries, allSessionIds),
    [filteredLogEntries, allSessionIds]
  );

  // Session ordering: sorted ascending by highSeverityRatio (normal bottom, dangerous top)
  const orderedSessionIds = useMemo(
    () => sessionMetas.map((m) => m.id),
    [sessionMetas]
  );

  // Quick lookup: sessionId -> meta
  const metaMap = useMemo(() => {
    const map: Record<string, SessionMeta> = {};

    sessionMetas.forEach((m) => {
      map[m.id] = m;
    });

    return map;
  }, [sessionMetas]);

  // Notify parent of session color map whenever it changes
  useEffect(() => {
    if (onSessionColorMapReady && sessionMetas.length > 0) {
      const colorMap: Record<string, string> = {};

      sessionMetas.forEach((m) => {
        colorMap[m.id] = m.color;
      });

      onSessionColorMapReady(colorMap);
    }
  }, [sessionMetas, onSessionColorMapReady]);

  const allBuckets = useMemo(
    () => buildBuckets(filteredLogEntries, allSessionIds, bucketSize),
    [filteredLogEntries, allSessionIds, bucketSize]
  );

  // Keep ref in sync for zoomOut callback
  allBucketsLengthRef.current = allBuckets.length;

  // Apply zoom range — show subset of buckets when zoomed
  const buckets = useMemo(() => {
    if (!sliceRange) {
      return allBuckets;
    }

    return allBuckets.slice(
      sliceRange.start,
      Math.min(allBuckets.length, sliceRange.end + 1)
    );
  }, [allBuckets, sliceRange]);

  // Recompute labels for visible buckets using visible time range
  // (so a 15-min slice shows HH:MM:SS, not HH:MM)
  const bucketsWithLabels = useMemo(() => {
    if (!sliceRange || buckets.length === 0) {
      return buckets;
    }

    const visRangeMin =
      buckets.length > 0
        ? (buckets[buckets.length - 1].epoch - buckets[0].epoch) / 60
        : 60;

    return buckets.map((b) => ({
      ...b,
      label: formatTickLabel(b.epoch, visRangeMin),
    }));
  }, [buckets, sliceRange]);

  const markers = useMemo(
    () => buildIncidentMarkers(findings, filteredLogEntries, allSessionIds),
    [findings, filteredLogEntries, allSessionIds]
  );

  // Filter markers to visible time range when zoomed
  const visibleMarkers = useMemo(() => {
    const filtered = markers.filter((m) => !hiddenSessions.has(m.sessionId));

    if (!isZoomed || buckets.length === 0) {
      return filtered;
    }

    const visStart = buckets[0]?.epoch ?? 0;
    const visEnd = buckets[buckets.length - 1]?.epoch ?? Infinity;

    return filtered.filter((m) => m.epoch >= visStart && m.epoch <= visEnd);
  }, [markers, hiddenSessions, isZoomed, buckets]);

  // Notify parent when slice time range changes — must be after visibleMarkers
  useEffect(() => {
    if (!onSliceChange) {
      return;
    }

    if (!sliceRange) {
      onSliceChange(null, undefined);
    } else if (buckets.length > 0) {
      const visIds = visibleMarkers.map((m) => m.findingId);

      onSliceChange(
        {
          startEpoch: buckets[0].epoch,
          endEpoch: buckets[buckets.length - 1].epoch,
        },
        visIds
      );
    }
  }, [sliceRange, buckets, onSliceChange, visibleMarkers]);

  // Max Y (stacked, respecting hidden sessions)
  const maxY = useMemo(() => {
    let max = 0;

    buckets.forEach((b) => {
      let stacked = 0;

      orderedSessionIds.forEach((sid) => {
        if (!hiddenSessions.has(sid)) {
          stacked += b.sessions[sid];
        }
      });

      if (stacked > max) {
        max = stacked;
      }
    });

    return Math.max(1, max);
  }, [buckets, orderedSessionIds, hiddenSessions]);

  // Apply session filter dropdown -> drives hiddenSessions
  const handleSessionFilterChange = useCallback(
    (value: string) => {
      setSessionFilter(value);

      if (value === 'all') {
        // Show everything
        setHiddenSessions(new Set());
      } else if (value === 'risky') {
        // Show only sessions with high-severity events (isWarm)
        const toHide = new Set<string>();

        sessionMetas.forEach((m) => {
          if (!m.isWarm) {
            toHide.add(m.id);
          }
        });

        setHiddenSessions(toHide);
      } else {
        // Single session selected — hide all others
        const toHide = new Set<string>();

        allSessionIds.forEach((sid) => {
          if (sid !== value) {
            toHide.add(sid);
          }
        });

        setHiddenSessions(toHide);
      }
    },
    [sessionMetas, allSessionIds]
  );

  // Close session dropdown on click outside
  useEffect(() => {
    if (!isSessionDropdownOpen) {
      return undefined;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (
        sessionDropdownRef.current &&
        !sessionDropdownRef.current.contains(e.target as Node)
      ) {
        setIsSessionDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSessionDropdownOpen]);

  // Mouse down handler — starts either selection (when not zoomed) or pan (when zoomed)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (hoveredMarker) {
        return;
      }

      e.preventDefault();
      setIsDragging(true);
      dragStartX.current = e.clientX;

      if (!sliceRange) {
        dragMode.current = 'select';
        // Don't show overlay yet — wait until mouse moves 5px (see handleMove)
      } else {
        dragMode.current = 'pan';
        dragStartSlice.current = { ...sliceRange };
      }
    },
    [hoveredMarker, sliceRange]
  );

  // Window mousemove/mouseup for drag interactions
  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMove = (e: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      if (dragMode.current === 'select') {
        const dx = Math.abs(e.clientX - dragStartX.current);

        // Only start showing overlay after 5px of movement
        if (dx < 5) {
          return;
        }

        // Map to plot area percentage
        const svgX = ((e.clientX - rect.left) / rect.width) * CHART_WIDTH;
        const plotStart = CHART_PADDING.left;
        const plotEnd = CHART_WIDTH - CHART_PADDING.right;
        const pct = Math.max(
          0,
          Math.min(100, ((svgX - plotStart) / (plotEnd - plotStart)) * 100)
        );

        setSelectionPct((prev) => {
          if (!prev) {
            // First time crossing threshold — create selection from drag start point
            const startSvgX =
              ((dragStartX.current - rect.left) / rect.width) * CHART_WIDTH;
            const startPct = Math.max(
              0,
              Math.min(
                100,
                ((startSvgX - plotStart) / (plotEnd - plotStart)) * 100
              )
            );

            return { start: startPct, end: pct };
          }

          return { ...prev, end: pct };
        });
      } else if (dragStartSlice.current && sliceRange) {
        const dx = e.clientX - dragStartX.current;

        const bucketsMoved = Math.round(
          (dx / rect.width) *
            (dragStartSlice.current.end - dragStartSlice.current.start)
        );
        const rangeSize =
          dragStartSlice.current.end - dragStartSlice.current.start;
        let newStart = dragStartSlice.current.start - bucketsMoved;
        let newEnd = dragStartSlice.current.end - bucketsMoved;

        // Clamp to allBuckets bounds
        if (newStart < 0) {
          newStart = 0;
          newEnd = rangeSize;
        }

        if (newEnd >= allBuckets.length) {
          newEnd = allBuckets.length - 1;
          newStart = newEnd - rangeSize;
        }

        setSliceRange({
          start: Math.max(0, newStart),
          end: Math.min(allBuckets.length - 1, newEnd),
        });
      }
    };

    const handleUp = () => {
      setIsDragging(false);

      if (dragMode.current === 'select') {
        setSelectionPct((prev) => {
          if (!prev) {
            return null;
          }

          const minPct = Math.min(prev.start, prev.end) / 100;
          const maxPct = Math.max(prev.start, prev.end) / 100;

          if (maxPct - minPct < 0.005) {
            return null;
          }

          const startIdx = Math.floor(minPct * allBuckets.length);
          const endIdx = Math.ceil(maxPct * allBuckets.length) - 1;

          if (endIdx > startIdx) {
            setSliceRange({ start: startIdx, end: endIdx });
          }

          return null;
        });
      }
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging, sliceRange, allBuckets.length]);

  const hasData = buckets.length > 0 && allSessionIds.length > 0;

  const handleChartApplicationKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Escape') {
        return;
      }

      e.stopPropagation();
      setSliceRange(null);
      setSelectionPct(null);
      setIsDragging(false);
    },
    []
  );

  // Coordinate helpers
  const plotW = CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
  const plotH = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;

  const xScale = (idx: number) =>
    CHART_PADDING.left + (idx / Math.max(1, buckets.length - 1)) * plotW;

  const yScale = (val: number) =>
    CHART_PADDING.top + plotH - (val / (maxY * 1.15)) * plotH;

  // Build stacked layers
  const stackedLayers = computeStackedLayers(
    buckets,
    orderedSessionIds,
    hiddenSessions,
    xScale,
    yScale
  );

  // Build a lookup for stacked cumulative values per bucket per session
  // (used for incident marker positioning)
  const getCumulativeAtBucket = (
    bucketIdx: number,
    sessionId: string
  ): { base: number; top: number } => {
    const valueForSid = (sid: string) =>
      hiddenSessions.has(sid) ? 0 : buckets[bucketIdx]?.sessions[sid] ?? 0;

    const idx = orderedSessionIds.indexOf(sessionId);

    if (idx < 0) {
      let total = 0;

      orderedSessionIds.forEach((sid) => {
        total += valueForSid(sid);
      });

      return { base: total, top: total };
    }

    let cumulative = 0;

    orderedSessionIds.slice(0, idx).forEach((sid) => {
      cumulative += valueForSid(sid);
    });

    const val = valueForSid(orderedSessionIds[idx]);

    return { base: cumulative, top: cumulative + val };
  };

  // Incident marker positions
  const markerPositions = visibleMarkers.map((m) => {
    const firstEpoch = buckets[0]?.epoch ?? 0;
    const lastEpoch = buckets[buckets.length - 1]?.epoch ?? 1;
    const range = lastEpoch - firstEpoch || 1;
    const xRatio = (m.epoch - firstEpoch) / range;
    const xPos = CHART_PADDING.left + xRatio * plotW;

    const bucketIdx = Math.min(
      buckets.length - 1,
      Math.max(0, Math.round(xRatio * (buckets.length - 1)))
    );

    const { base, top } = getCumulativeAtBucket(bucketIdx, m.sessionId);
    const midVal = base + (top - base) / 2;
    const yPos = yScale(midVal);

    return { ...m, x: xPos, y: yPos, bucketIdx };
  });

  // Y-axis ticks
  const yTickCount = 5;
  const yStep = Math.ceil((maxY * 1.15) / yTickCount);
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => i * yStep);

  // X-axis labels
  const xLabelInterval = Math.max(1, Math.floor(buckets.length / 10));

  // Crosshair bucket data
  const crosshairBucket = hoveredBucket
    ? bucketsWithLabels[hoveredBucket.idx]
    : null;

  // Count visible sessions
  const visibleCount = allSessionIds.length - hiddenSessions.size;

  return (
    <Box className={cx('Sod-SessionAreaChart', className)}>
      {/* Header */}
      <Box className='Sod-SessionAreaChart-header'>
        <Box className='Sod-SessionAreaChart-header-left'>
          <Box>
            <Typography kind='h3' className='Sod-SessionAreaChart-title'>
              {TRANSACTION_INSIGHT}
            </Typography>
            <div className='Sod-SessionAreaChart-subtitle'>
              <span className='Sod-SessionAreaChart-chip Sod-SessionAreaChart-chip--sessions'>
                {allSessionIds.length} {SESSIONS_PREFIX}
                {visibleCount} {VISIBLE_SUFFIX}
              </span>
              <span className='Sod-SessionAreaChart-chip Sod-SessionAreaChart-chip--incidents'>
                {visibleMarkers.length} {INCIDENTS}
              </span>
              <span className='Sod-SessionAreaChart-chip Sod-SessionAreaChart-chip--critical'>
                {visibleMarkers.filter((m) => m.severity === 'CRITICAL').length}{' '}
                {CRITICAL}
              </span>
              <span className='Sod-SessionAreaChart-chip Sod-SessionAreaChart-chip--high'>
                {visibleMarkers.filter((m) => m.severity === 'HIGH').length}{' '}
                {HIGH}
              </span>
              <span className='Sod-SessionAreaChart-chip Sod-SessionAreaChart-chip--medium'>
                {visibleMarkers.filter((m) => m.severity === 'MEDIUM').length}{' '}
                {MEDIUM}
              </span>
            </div>
          </Box>
        </Box>
        {/* Date & Time filter — toggled via time icon with fade animation */}
        <div
          className={cx('Sod-SessionAreaChart-header-dateFilter', {
            'Sod-SessionAreaChart-header-dateFilter--visible':
              isDateFilterVisible,
          })}>
          <DateAndTimePicker
            kind='dateAndTimeStart'
            label=''
            startDate={filterStartDate}
            setStartDate={(date: Date | null) => {
              setFilterStartDate(date);
            }}
            endDate={filterEndDate}
            setEndDate={(date: Date | null) => {
              setFilterEndDate(date);
            }}
            maxDate={dateRangeMax}
            minDate={dateRangeMin}
            dateFormat='MM/dd/yyyy, h:mm aa'
            portalId='root'
          />
          <span className='Sod-SessionAreaChart-header-dateTo'>{TO_LABEL}</span>
          <DateAndTimePicker
            kind='dateAndTimeEnd'
            label=''
            startDate={filterStartDate}
            setStartDate={(date: Date | null) => {
              setFilterStartDate(date);
            }}
            endDate={filterEndDate}
            setEndDate={(date: Date | null) => {
              setFilterEndDate(date);
            }}
            maxDate={dateRangeMax}
            minDate={dateRangeMin}
            dateFormat='MM/dd/yyyy, h:mm aa'
            portalId='root'
          />
        </div>

        <Box className='Sod-SessionAreaChart-header-controls'>
          {/* Zoom controls */}
          {isZoomed && (
            <>
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('Reset zoom')}
                icon={<Icon kind='Reset' size='medium' />}
                onClick={resetZoom}
              />
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('Zoom out')}
                icon={<Icon kind='ZoomOut' size='medium' />}
                onClick={zoomOut}
              />
            </>
          )}
          <ButtonIcon
            size='medium'
            kind='ghost'
            isSelected={isDateFilterVisible}
            ariaLabel={l('Toggle date filter')}
            icon={<Icon kind='Time' size='medium' />}
            onClick={() => setIsDateFilterVisible((prev) => !prev)}
          />
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={l('Zoom in')}
            icon={<Icon kind='ZoomIn' size='medium' />}
            onClick={zoomIn}
            isDisabled={buckets.length <= 2}
          />

          {/* Bucket size dropdown — DS Menu + ButtonSelect */}
          <Menu
            kind='simple'
            options={BUCKET_SIZE_OPTIONS.map((o) => ({
              label: `${o.label} intervals`,
              value: String(o.value),
              isChecked: o.value === bucketSize,
            }))}
            onChange={(option: { value: string }) => {
              setBucketSize(Number(option.value));
            }}
            trigger={
              <ButtonSelect
                kind='rectangle'
                label={`${
                  BUCKET_SIZE_OPTIONS.find((o) => o.value === bucketSize)
                    ?.label ?? ''
                } intervals`}
                size='small'
              />
            }
            placement='bottom-end'
          />

          {/* Session filter — custom multi-select dropdown */}
          <div
            className='Sod-SessionAreaChart-sessionDropdown'
            ref={sessionDropdownRef}>
            <ButtonSelect
              kind='rectangle'
              label={
                hiddenSessions.size === 0
                  ? `All sessions (${allSessionIds.length})`
                  : `${allSessionIds.length - hiddenSessions.size} of ${
                      allSessionIds.length
                    } sessions`
              }
              size='small'
              isOpen={isSessionDropdownOpen}
              onClick={() => setIsSessionDropdownOpen((prev) => !prev)}
            />
            {isSessionDropdownOpen && (
              <div className='Sod-SessionAreaChart-sessionDropdown-panel'>
                {/* Quick filters */}
                <div className='Sod-SessionAreaChart-sessionDropdown-quickRow'>
                  <button
                    type='button'
                    className={cx(
                      'Sod-SessionAreaChart-sessionDropdown-quickBtn',
                      {
                        'Sod-SessionAreaChart-sessionDropdown-quickBtn--active':
                          hiddenSessions.size === 0,
                      }
                    )}
                    onClick={() => {
                      handleSessionFilterChange('all');
                    }}>
                    {ALL_LABEL}
                  </button>
                  <button
                    type='button'
                    className={cx(
                      'Sod-SessionAreaChart-sessionDropdown-quickBtn',
                      'Sod-SessionAreaChart-sessionDropdown-quickBtn--risky',
                      {
                        'Sod-SessionAreaChart-sessionDropdown-quickBtn--active':
                          sessionFilter === 'risky',
                      }
                    )}
                    onClick={() => handleSessionFilterChange('risky')}>
                    {RISKY_PREFIX}
                    {sessionMetas.filter((m) => m.isWarm).length}
                    {CLOSE_PAREN}
                  </button>
                </div>
                {/* Session list with checkboxes */}
                <div className='Sod-SessionAreaChart-sessionDropdown-list'>
                  {sessionMetas.map((m) => {
                    const isChecked = !hiddenSessions.has(m.id);

                    const sessionVisibilityId = `Sod-SessionAreaChart-session-vis-${m.id}`;

                    return (
                      <label
                        key={m.id}
                        htmlFor={sessionVisibilityId}
                        className={cx(
                          'Sod-SessionAreaChart-sessionDropdown-item',
                          {
                            'Sod-SessionAreaChart-sessionDropdown-item--checked':
                              isChecked,
                            'Sod-SessionAreaChart-sessionDropdown-item--risky':
                              m.isWarm,
                          }
                        )}>
                        <input
                          id={sessionVisibilityId}
                          type='checkbox'
                          checked={isChecked}
                          onChange={() => {
                            setSessionFilter('custom');
                            setHiddenSessions((prev) => {
                              const next = new Set(prev);

                              if (next.has(m.id)) {
                                next.delete(m.id);
                              } else {
                                next.add(m.id);
                              }

                              return next;
                            });
                          }}
                          className='Sod-SessionAreaChart-sessionDropdown-checkbox'
                        />
                        <span
                          className='Sod-SessionAreaChart-sessionDropdown-color'
                          style={{ backgroundColor: m.color }}
                        />
                        <span className='Sod-SessionAreaChart-sessionDropdown-sid'>
                          {SESSION_PREFIX}
                          {m.id}
                        </span>
                        <span className='Sod-SessionAreaChart-sessionDropdown-evtCount'>
                          {m.totalEvents}
                        </span>
                        {m.highSeverityCount > 0 && (
                          <span className='Sod-SessionAreaChart-sessionDropdown-sevBadge'>
                            {m.highSeverityCount}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Box>
      </Box>

      {/* Chart — custom drag/zoom widget; Escape clears zoom/selection (role=application + tabIndex not treated as interactive by eslint-plugin-jsx-a11y) */}
      {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        className={cx('Sod-SessionAreaChart-chartWrap', {
          'Sod-SessionAreaChart-chartWrap--crosshair':
            hasData && !sliceRange && !isDragging,
          'Sod-SessionAreaChart-chartWrap--selecting':
            hasData && !sliceRange && isDragging,
          'Sod-SessionAreaChart-chartWrap--grab':
            hasData && sliceRange && !isDragging,
          'Sod-SessionAreaChart-chartWrap--grabbing':
            hasData && sliceRange && isDragging,
        })}
        ref={containerRef}
        role={hasData ? 'application' : undefined}
        aria-label={hasData ? l('Session activity timeline chart') : undefined}
        tabIndex={hasData ? 0 : undefined}
        onKeyDown={hasData ? handleChartApplicationKeyDown : undefined}
        onMouseDown={hasData ? handleMouseDown : undefined}>
        <svg
          className='Sod-SessionAreaChart-svg'
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          preserveAspectRatio='xMidYMid meet'
          onMouseMove={
            hasData
              ? (e: React.MouseEvent<SVGSVGElement>) => {
                  const rect = containerRef.current?.getBoundingClientRect();

                  if (!rect) {
                    return;
                  }

                  const svgX =
                    ((e.clientX - rect.left) / rect.width) * CHART_WIDTH;
                  const idx = Math.round(
                    ((svgX - CHART_PADDING.left) / plotW) * (buckets.length - 1)
                  );

                  if (idx >= 0 && idx < buckets.length) {
                    setHoveredBucket({
                      idx,
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                    });
                  } else {
                    setHoveredBucket(null);
                  }
                }
              : undefined
          }
          onMouseLeave={
            hasData
              ? () => {
                  setHoveredBucket(null);
                }
              : undefined
          }
          onClick={
            hasData
              ? () => {
                  // Fallback: if a marker is hovered when SVG is clicked, treat it as a marker click
                  if (hoveredMarker) {
                    onIncidentClick?.(
                      hoveredMarker.severity,
                      hoveredMarker.findingId,
                      Object.fromEntries(
                        Object.entries(metaMap).map(([sid, m]) => [
                          sid,
                          m.color,
                        ])
                      )
                    );
                  }
                }
              : undefined
          }>
          {/* Empty state — blank chart with axes */}
          {!hasData && (
            <>
              {/* Grid lines for empty state */}
              {[0, 1, 2, 3, 4, 5].map((i) => {
                const y = CHART_PADDING.top + (plotH / 5) * (5 - i);

                return (
                  <g key={`empty-ygrid-${i}`}>
                    <line
                      x1={CHART_PADDING.left}
                      y1={y}
                      x2={CHART_PADDING.left + plotW}
                      y2={y}
                      className='Sod-SessionAreaChart-gridLine'
                    />
                    <text
                      x={CHART_PADDING.left - 10}
                      y={y + 4}
                      className='Sod-SessionAreaChart-yTick'>
                      {i * 10}
                    </text>
                  </g>
                );
              })}
              {/* Baseline */}
              <line
                x1={CHART_PADDING.left}
                y1={CHART_PADDING.top + plotH}
                x2={CHART_PADDING.left + plotW}
                y2={CHART_PADDING.top + plotH}
                stroke='var(--color-border-neutral-subtle)'
                strokeWidth={1}
              />
              {/* Y-axis label */}
              <text
                x={16}
                y={CHART_PADDING.top + plotH / 2}
                className='Sod-SessionAreaChart-axisLabel'
                transform={`rotate(-90, 16, ${CHART_PADDING.top + plotH / 2})`}>
                {EVENTS_PER_BUCKET}
              </text>
              {/* X-axis label */}
              <text
                x={CHART_PADDING.left + plotW / 2}
                y={CHART_HEIGHT - 6}
                className='Sod-SessionAreaChart-axisLabel'>
                {TIME_LABEL}
              </text>
              {/* No data message */}
              <text
                x={CHART_PADDING.left + plotW / 2}
                y={CHART_PADDING.top + plotH / 2}
                textAnchor='middle'
                className='Sod-SessionAreaChart-emptyText'>
                {NO_DATA_MSG}
              </text>
            </>
          )}

          {/* Data-dependent chart content */}
          {hasData && (
            <>
              {/* Grid lines */}
              {yTicks.map((tick) => {
                const y = yScale(tick);

                return (
                  <g key={`ygrid-${tick}`}>
                    <line
                      x1={CHART_PADDING.left}
                      y1={y}
                      x2={CHART_PADDING.left + plotW}
                      y2={y}
                      className='Sod-SessionAreaChart-gridLine'
                    />
                    <text
                      x={CHART_PADDING.left - 10}
                      y={y + 4}
                      className='Sod-SessionAreaChart-yTick'>
                      {tick}
                    </text>
                  </g>
                );
              })}

              {/* Y-axis label */}
              <text
                x={16}
                y={CHART_PADDING.top + plotH / 2}
                className='Sod-SessionAreaChart-axisLabel'
                transform={`rotate(-90, 16, ${CHART_PADDING.top + plotH / 2})`}>
                {EVENTS_PER_BUCKET}
              </text>

              {/* Stacked areas — render bottom to top */}
              {stackedLayers.map((layer) => {
                const meta = metaMap[layer.sessionId];

                if (hiddenSessions.has(layer.sessionId)) {
                  return null;
                }

                return (
                  <g key={`area-${layer.sessionId}`}>
                    <path
                      d={buildAreaPath(layer.points)}
                      fill={meta.fillColor}
                      className='Sod-SessionAreaChart-area'
                    />
                  </g>
                );
              })}

              {/* X-axis labels — always show first and last, plus evenly spaced */}
              {bucketsWithLabels.map((b, i) => {
                const isFirst = i === 0;
                const isLast = i === bucketsWithLabels.length - 1;
                const isInterval = i % xLabelInterval === 0;

                if (!isFirst && !isLast && !isInterval) {
                  return null;
                }

                return (
                  <text
                    key={`xlabel-${b.epoch}`}
                    x={xScale(i)}
                    y={CHART_PADDING.top + plotH + 18}
                    className='Sod-SessionAreaChart-xTick'>
                    {b.label}
                  </text>
                );
              })}

              {/* X-axis title */}
              <text
                x={CHART_PADDING.left + plotW / 2}
                y={CHART_HEIGHT - 6}
                className='Sod-SessionAreaChart-axisLabel'>
                {TIME_PAREN}
                {
                  BUCKET_SIZE_OPTIONS.find((o) => o.value === bucketSize)?.label
                }{' '}
                {INTERVALS_CLOSE}
              </text>

              {/* Baseline */}
              <line
                x1={CHART_PADDING.left}
                y1={yScale(0)}
                x2={CHART_PADDING.left + plotW}
                y2={yScale(0)}
                stroke='var(--color-border-neutral-subtle)'
                strokeWidth={1}
              />

              {/* Crosshair */}
              {hoveredBucket && (
                <g style={{ pointerEvents: 'none' }}>
                  <line
                    x1={xScale(hoveredBucket.idx)}
                    y1={CHART_PADDING.top}
                    x2={xScale(hoveredBucket.idx)}
                    y2={yScale(0)}
                    stroke='var(--color-foreground-neutral-subtle)'
                    strokeWidth={1}
                    strokeDasharray='4 3'
                    opacity={0.5}
                  />
                  {/* Dots on each visible layer's top line */}
                  {stackedLayers.map((layer) => {
                    if (hiddenSessions.has(layer.sessionId)) {
                      return null;
                    }

                    const pt = layer.points[hoveredBucket.idx];
                    const meta = metaMap[layer.sessionId];

                    return (
                      <circle
                        key={`dot-${layer.sessionId}`}
                        cx={pt.x}
                        cy={pt.y1}
                        r={3}
                        fill={meta.color}
                        stroke='var(--color-background-neutral-subtlest)'
                        strokeWidth={1.5}
                      />
                    );
                  })}
                </g>
              )}

              {/* Incident markers */}
              {markerPositions.map((m) => {
                const isCritical = m.severity === 'CRITICAL';
                const isHigh = m.severity === 'HIGH';
                let markerSize = 4;

                if (isCritical) {
                  markerSize = 6;
                } else if (isHigh) {
                  markerSize = 5;
                }

                return (
                  <g
                    key={`incident-${m.findingId}`}
                    onMouseEnter={() => setHoveredMarker(m)}
                    onMouseLeave={() => setHoveredMarker(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onIncidentClick?.(
                        m.severity,
                        m.findingId,
                        Object.fromEntries(
                          Object.entries(metaMap).map(([sid, mt]) => [
                            sid,
                            mt.color,
                          ])
                        )
                      );
                    }}
                    style={{ cursor: 'pointer', pointerEvents: 'all' }}>
                    {/* Invisible enlarged hit target for easier clicking */}
                    <circle
                      cx={m.x}
                      cy={m.y}
                      r={Math.max(12, markerSize + 6)}
                      fill='transparent'
                      style={{ pointerEvents: 'all' }}
                    />
                    {/* Vertical drop line */}
                    <line
                      x1={m.x}
                      y1={m.y}
                      x2={m.x}
                      y2={yScale(0)}
                      stroke={SEVERITY_COLORS[m.severity]}
                      strokeWidth={1}
                      strokeDasharray='3 2'
                      opacity={0.4}
                    />

                    {/* Pulse ring for critical */}
                    {isCritical && (
                      <circle
                        cx={m.x}
                        cy={m.y}
                        r={markerSize + 3}
                        fill='none'
                        stroke={SEVERITY_COLORS.CRITICAL}
                        strokeWidth={1}
                        opacity={0.3}
                        className='Sod-SessionAreaChart-pulseRing'
                      />
                    )}

                    {/* Outer ring */}
                    <circle
                      cx={m.x}
                      cy={m.y}
                      r={markerSize}
                      fill='var(--color-background-neutral-subtlest)'
                      stroke={SEVERITY_COLORS[m.severity]}
                      strokeWidth={2}
                    />

                    {/* Inner dot */}
                    <circle
                      cx={m.x}
                      cy={m.y}
                      r={markerSize - 3}
                      fill={SEVERITY_COLORS[m.severity]}
                    />
                  </g>
                );
              })}
            </>
          )}
        </svg>

        {/* Selection overlay for drag-to-slice */}
        {hasData &&
          selectionPct &&
          isDragging &&
          dragMode.current === 'select' &&
          (() => {
            const selPlotW =
              CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
            const minPct = Math.min(selectionPct.start, selectionPct.end) / 100;
            const maxPct = Math.max(selectionPct.start, selectionPct.end) / 100;
            const leftSvg = CHART_PADDING.left + minPct * selPlotW;
            const widthSvg = (maxPct - minPct) * selPlotW;
            const leftContainerPct = (leftSvg / CHART_WIDTH) * 100;
            const widthContainerPct = (widthSvg / CHART_WIDTH) * 100;

            return (
              <div
                className='Sod-SessionAreaChart-selectionOverlay'
                style={{
                  left: `${leftContainerPct}%`,
                  width: `${Math.max(2, widthContainerPct)}%`,
                }}
              />
            );
          })()}

        {/* Hover tooltip -- crosshair */}
        {hoveredBucket && crosshairBucket && !hoveredMarker && (
          <div
            className='Sod-SessionAreaChart-tooltip'
            style={{
              left: Math.min(
                hoveredBucket.x + 16,
                (containerRef.current?.clientWidth ?? 900) - 220
              ),
              top: Math.max(8, hoveredBucket.y - 60),
            }}>
            <div className='Sod-SessionAreaChart-tooltip-time'>
              {crosshairBucket.label}
            </div>
            <div className='Sod-SessionAreaChart-tooltip-divider' />
            <div className='Sod-SessionAreaChart-tooltip-list'>
              {orderedSessionIds
                .filter((sid) => !hiddenSessions.has(sid))
                .map((sid) => {
                  const count = crosshairBucket.sessions[sid];

                  if (count === 0) {
                    return null;
                  }

                  const meta = metaMap[sid];

                  return (
                    <div key={sid} className='Sod-SessionAreaChart-tooltip-row'>
                      <span
                        className='Sod-SessionAreaChart-tooltip-swatch'
                        style={{ backgroundColor: meta.color }}
                      />
                      <span>
                        {SESSION_PREFIX}
                        {sid}
                      </span>
                      <span className='Sod-SessionAreaChart-tooltip-val'>
                        {count} {EVENTS_LABEL}
                      </span>
                    </div>
                  );
                })}
            </div>
            <div className='Sod-SessionAreaChart-tooltip-total'>
              {TOTAL_COLON}{' '}
              {orderedSessionIds
                .filter((sid) => !hiddenSessions.has(sid))
                .reduce((sum, sid) => sum + crosshairBucket.sessions[sid], 0)}
            </div>
          </div>
        )}

        {/* Hover tooltip -- incident marker */}
        {hoveredMarker && (
          <div
            className='Sod-SessionAreaChart-incidentTooltip'
            style={{
              left: Math.min(
                ((hoveredMarker as unknown as { x: number }).x / CHART_WIDTH) *
                  (containerRef.current?.clientWidth ?? 900) +
                  16,
                (containerRef.current?.clientWidth ?? 900) - 280
              ),
              top: 20,
            }}>
            <div className='Sod-SessionAreaChart-incidentTooltip-header'>
              <span
                className='Sod-SessionAreaChart-incidentTooltip-severity'
                style={{
                  color: SEVERITY_COLORS[hoveredMarker.severity],
                  backgroundColor: `color-mix(in srgb, ${
                    SEVERITY_COLORS[hoveredMarker.severity]
                  } 15%, transparent)`,
                }}>
                {hoveredMarker.severity}
              </span>
              <span className='Sod-SessionAreaChart-incidentTooltip-risk'>
                {RISK_LABEL} {hoveredMarker.riskScore}
              </span>
            </div>
            <div className='Sod-SessionAreaChart-incidentTooltip-title'>
              {HASH}
              {hoveredMarker.findingId} {hoveredMarker.title}
            </div>
            <div className='Sod-SessionAreaChart-incidentTooltip-meta'>
              <span>
                {PHASE_LABELS[hoveredMarker.phase] || hoveredMarker.phase}
              </span>
              <span>{MIDDOT}</span>
              <span>
                {SESSION_WORD} {hoveredMarker.sessionId}
              </span>
            </div>
          </div>
        )}
        {/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      </div>
    </Box>
  );
}

SessionAreaChart.defaultProps = defaultProps;

export default SessionAreaChart;
